import { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  Calculator,
  AlertTriangle,
  CalendarDays,
  Check,
  CheckCircle2,
  Eye,
  Loader2,
  ShieldCheck,
  XCircle,
} from 'lucide-react';
import { api } from '@/shared/api/cliente';
import { ErrorDeApi } from '@/shared/api/problema';
import type { Emision, PreviaDeEmision } from '@/shared/api/tipos';
import { Modal } from '@/shared/ui/Modal';
import { notificar } from '@/shared/ui/avisos';
import { SelectorDeMes, mesAnterior } from '@/shared/ui/SelectorDeMes';
import { nombreLegible } from '@/modules/tarifario/nombres';

const PASOS = [
  { titulo: 'Periodo', detalle: 'Qué mes se cobra', icono: CalendarDays },
  { titulo: 'Verificar', detalle: 'Qué se va a generar', icono: Eye },
  { titulo: 'Confirmar', detalle: 'Última revisión', icono: ShieldCheck },
  { titulo: 'Resultado', detalle: 'Resumen del lote', icono: CheckCircle2 },
];

export function AsistenteDeEmision({
  abierto,
  yaEmitidos,
  alCerrar,
  alTerminar,
}: {
  abierto: boolean;
  yaEmitidos: string[];
  alCerrar: () => void;
  alTerminar: () => void;
}) {
  const [paso, setPaso] = useState(0);
  const [periodo, setPeriodo] = useState(mesAnterior());
  const [anio, setAnio] = useState(Number(mesAnterior().slice(0, 4)));
  const [error, setError] = useState<string | null>(null);

  const [simulando, setSimulando] = useState(false);

  const previa = useQuery({
    queryKey: ['emision-previa', periodo, simulando],
    queryFn: () =>
      api.get<PreviaDeEmision>(`/emisiones/${periodo}/previa`, {
        query: { calcular: simulando },
      }),
    enabled: abierto && paso >= 1,
  });

  const emitir = useMutation({
    mutationFn: () => api.post<Emision>('/emisiones', { periodo }),
    onSuccess: (e) => {
      setError(null);
      setPaso(3);
      alTerminar();
      notificar.exito(
        `Periodo ${e.periodo} emitido: ${e.facturasGeneradas.toLocaleString('es-EC')} facturas`,
      );
    },
    onError: (causa) => {
      const mensaje = causa instanceof ErrorDeApi ? causa.message : 'No se pudo emitir';
      setError(mensaje);
      notificar.error(mensaje);
    },
  });

  function cerrarYReiniciar() {
    alCerrar();
    setPaso(0);
    setError(null);
    emitir.reset();
  }

  const puedeAvanzar =
    paso === 0 ? periodo !== '' : paso === 1 ? previa.data?.sePuedeEmitir === true : paso === 2;

  return (
    <Modal
      abierto={abierto}
      alCerrar={emitir.isPending ? () => {} : cerrarYReiniciar}
      titulo="Emitir un periodo"
      subtitulo={PASOS[paso]?.detalle ?? ''}
      icono={CalendarDays}
      cerrarAlPulsarFuera={false}
    >
      <div className="space-y-5">
        <ol className="flex items-center gap-1">
          {PASOS.map((p, i) => {
            const Icono = p.icono;
            const hecho = paso > i;
            const activo = paso === i;

            return (
              <li key={p.titulo} className="flex flex-1 items-center gap-1">
                <div className="flex min-w-0 flex-1 flex-col items-center gap-1">
                  <span
                    className={`grid size-8 shrink-0 place-items-center rounded-full transition-colors ${
                      hecho
                        ? 'bg-emerald-500 text-white'
                        : activo
                          ? 'bg-marca-600 text-white'
                          : 'bg-armazon-100 text-armazon-400'
                    }`}
                  >
                    {hecho ? (
                      <Check className="size-4" aria-hidden />
                    ) : (
                      <Icono className="size-4" aria-hidden />
                    )}
                  </span>
                  <span
                    className={`truncate text-[10px] font-semibold uppercase ${
                      activo ? 'text-armazon-800' : 'text-armazon-400'
                    }`}
                  >
                    {p.titulo}
                  </span>
                </div>
                {i < PASOS.length - 1 && (
                  <span
                    aria-hidden
                    className={`mb-4 h-0.5 w-full flex-1 rounded ${
                      paso > i ? 'bg-emerald-500' : 'bg-armazon-100'
                    }`}
                  />
                )}
              </li>
            );
          })}
        </ol>

        {error && (
          <p
            role="alert"
            className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-900"
          >
            {error}
          </p>
        )}

        {paso === 0 && (
          <div className="space-y-3">
            <SelectorDeMes
              valor={periodo}
              alCambiar={setPeriodo}
              anio={anio}
              alCambiarAnio={setAnio}
              marcados={yaEmitidos}
              leyenda="ya emitido"
              accion="Se cobrará"
              maximo={mesAnterior()}
            />
            <p className="text-justify text-[11px] uppercase leading-relaxed text-armazon-500">
              Se cobra el mes que terminó, no el que corre: agosto se emite en septiembre y se paga
              durante todo septiembre. Los meses que aún no han terminado están bloqueados.
            </p>
          </div>
        )}

        {paso === 1 && (
          <div className="space-y-3">
            {previa.isLoading ? (
              <p className="rounded-lg border border-armazon-200 py-8 text-center text-xs uppercase text-armazon-400">
                Consultando el padrón…
              </p>
            ) : previa.data ? (
              <>
                <div className="grid grid-cols-3 gap-2">
                  <Dato
                    valor={previa.data.facturables}
                    rotulo="Clientes facturables"
                    nota="Activos con predio"
                  />
                  <Dato
                    valor={previa.data.yaFacturados}
                    rotulo="Ya facturados"
                    nota="Se omitirán"
                  />
                  <Dato valor={previa.data.porFacturar} rotulo="Recibirán factura" destacado />
                </div>

                {previa.data.calculado ? (
                  <Simulacro previa={previa.data} />
                ) : (
                  <button
                    type="button"
                    onClick={() => setSimulando(true)}
                    disabled={previa.isFetching}
                    className="flex w-full items-center justify-center gap-2 rounded-lg border border-marca-300 bg-marca-50 px-3 py-2.5 text-[11px] font-bold uppercase text-marca-700 transition hover:bg-marca-100 disabled:opacity-50"
                  >
                    <Calculator className="size-4" aria-hidden />
                    {previa.isFetching
                      ? 'Recorriendo el padrón…'
                      : 'Calcular lo que se va a cobrar'}
                  </button>
                )}

                <div className="rounded-lg border border-armazon-200 bg-armazon-50/60 px-3 py-2">
                  <p className="text-[11px] uppercase text-armazon-600">
                    Vencerá el{' '}
                    <span className="importe font-semibold text-armazon-800">
                      {formatearLargo(previa.data.vencimiento)}
                    </span>
                    , último día del mes siguiente al periodo.
                  </p>
                </div>

                {!previa.data.sePuedeEmitir && previa.data.anterior && (
                  <div className="flex gap-3 rounded-lg border border-red-200 bg-red-50 p-3">
                    <XCircle className="mt-0.5 size-4 shrink-0 text-red-600" aria-hidden />
                    <p className="text-justify text-[11px] uppercase leading-relaxed text-red-800">
                      <span className="font-semibold">Este periodo ya se emitió</span> el{' '}
                      {formatearLargo(previa.data.anterior.fechaEmision)}, con{' '}
                      {previa.data.anterior.facturasGeneradas.toLocaleString('es-EC')} facturas por
                      ${previa.data.anterior.montoTotal.toFixed(2)}. Para volver a emitirlo hay que
                      anular esas facturas primero.
                    </p>
                  </div>
                )}
              </>
            ) : (
              <p className="rounded-lg border border-red-200 bg-red-50 p-3 text-xs uppercase text-red-800">
                No se pudo consultar el padrón
              </p>
            )}
          </div>
        )}

        {paso === 2 && previa.data && (
          <div className="space-y-3">
            <div className="rounded-xl border border-armazon-200 p-4 text-center">
              <p className="importe text-3xl font-bold text-marca-700">
                {previa.data.porFacturar.toLocaleString('es-EC')}
              </p>
              <p className="text-[11px] font-semibold uppercase text-armazon-600">
                facturas del periodo {previa.data.periodo}
              </p>
              <p className="mt-1 text-[10px] uppercase text-armazon-400">
                Vencen el {formatearLargo(previa.data.vencimiento)}
              </p>
            </div>

            <div className="flex gap-3 rounded-lg border border-amber-200 bg-amber-50 p-3">
              <AlertTriangle className="mt-0.5 size-4 shrink-0 text-amber-600" aria-hidden />
              <p className="text-justify text-[11px] uppercase leading-relaxed text-amber-800">
                <span className="font-semibold">Esto genera deuda a nombre de cada cliente.</span>{' '}
                Un periodo no se puede cobrar dos veces, y deshacerlo exige anular las facturas una
                a una. Sobre el padrón completo tarda alrededor de minuto y medio: no cierre la
                ventana mientras corre.
              </p>
            </div>
          </div>
        )}

        {paso === 3 && emitir.data && (
          <div className="space-y-3">
            <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4 text-center">
              <CheckCircle2 className="mx-auto mb-2 size-8 text-emerald-600" aria-hidden />
              <p className="importe text-2xl font-bold text-emerald-700">
                {emitir.data.facturasGeneradas.toLocaleString('es-EC')} facturas
              </p>
              <p className="text-[11px] font-semibold uppercase text-emerald-800">
                Periodo {emitir.data.periodo} emitido en {emitir.data.duracionSegundos} s
              </p>
            </div>

            <div className="grid grid-cols-3 gap-2">
              <Dato valor={emitir.data.omitidos} rotulo="Omitidos" nota="Ya facturados" />
              <Dato
                valor={emitir.data.conError}
                rotulo="Con error"
                nota="Sin tarifa"
                alerta={emitir.data.conError > 0}
              />
              <Dato
                valor={emitir.data.montoTotal}
                rotulo="Total"
                nota="A cobrar"
                dinero
                destacado
              />
            </div>
          </div>
        )}

        <div className="sticky bottom-0 -mx-6 -mb-5 mt-6 flex items-center justify-between gap-2 border-t border-armazon-100 bg-white px-6 py-4">
          <button
            type="button"
            onClick={() => (paso === 0 || paso === 3 ? cerrarYReiniciar() : setPaso(paso - 1))}
            disabled={emitir.isPending}
            className="rounded-lg px-4 py-2 text-sm font-semibold uppercase text-armazon-600 transition-colors hover:bg-armazon-100 disabled:opacity-40"
          >
            {paso === 0 ? 'Cancelar' : paso === 3 ? 'Cerrar' : 'Atrás'}
          </button>

          {paso < 3 && (
            <button
              type="button"
              onClick={() => (paso === 2 ? emitir.mutate() : setPaso(paso + 1))}

              disabled={!puedeAvanzar || emitir.isPending || previa.isLoading}
              className="inline-flex items-center gap-2 rounded-lg bg-marca-600 px-4 py-2 text-sm font-semibold uppercase text-white transition-colors hover:bg-marca-700 disabled:opacity-40"
            >
              {emitir.isPending && <Loader2 className="size-4 animate-spin" aria-hidden />}
              {paso === 2 ? (emitir.isPending ? 'Emitiendo…' : 'Emitir') : 'Continuar'}
            </button>
          )}
        </div>
      </div>
    </Modal>
  );
}

function Dato({
  valor,
  rotulo,
  nota,
  destacado = false,
  alerta = false,
  dinero = false,
}: {
  valor: number;
  rotulo: string;
  nota?: string;
  destacado?: boolean;
  alerta?: boolean;
  dinero?: boolean;
}) {
  return (
    <div
      className={`rounded-lg border px-3 py-2 text-center ${
        alerta ? 'border-amber-200 bg-amber-50' : 'border-armazon-200'
      }`}
    >
      <p
        className={`importe text-lg font-bold ${
          alerta ? 'text-amber-700' : destacado ? 'text-marca-700' : 'text-armazon-800'
        }`}
      >
        {dinero ? `$${valor.toFixed(2)}` : valor.toLocaleString('es-EC')}
      </p>
      <p className="text-[10px] font-semibold uppercase text-armazon-600">{rotulo}</p>
      {nota && <p className="text-[9px] uppercase text-armazon-400">{nota}</p>}
    </div>
  );
}

function formatearLargo(iso: string): string {
  const [anio, mes, dia] = iso.split('-');
  const MESES = [
    'enero',
    'febrero',
    'marzo',
    'abril',
    'mayo',
    'junio',
    'julio',
    'agosto',
    'septiembre',
    'octubre',
    'noviembre',
    'diciembre',
  ];
  return `${Number(dia)} de ${MESES[Number(mes) - 1] ?? mes} de ${anio}`;
}

function Simulacro({ previa }: { previa: PreviaDeEmision }) {
  const total = previa.total ?? 0;
  const anterior = previa.delMesAnterior;

  const variacion = anterior && anterior > 0 ? ((total - anterior) / anterior) * 100 : null;
  const salta = variacion !== null && Math.abs(variacion) >= 10;

  return (
    <div className="space-y-2">
      <div className="rounded-lg border border-marca-200 bg-marca-50/60 p-3 text-center">
        <p className="text-[10px] font-semibold uppercase tracking-wide text-marca-700">
          Se cobrará
        </p>
        <p className="importe text-2xl font-bold text-marca-800">
          ${total.toLocaleString('es-EC', { minimumFractionDigits: 2 })}
        </p>
        <p className="text-[10px] uppercase text-armazon-500">
          {previa.facturas.toLocaleString('es-EC')} facturas
          {previa.descuento !== null && previa.descuento > 0 && (
            <> · ${previa.descuento.toFixed(2)} exonerados</>
          )}
        </p>
      </div>

      {anterior !== null && (
        <p
          className={`rounded-lg border px-3 py-2 text-[11px] uppercase ${
            salta
              ? 'border-amber-200 bg-amber-50 text-amber-800'
              : 'border-armazon-200 text-armazon-600'
          }`}
        >
          El mes anterior fueron{' '}
          <span className="importe font-semibold">${anterior.toFixed(2)}</span>
          {variacion !== null && (
            <>
              {' '}
              ({variacion > 0 ? '+' : ''}
              {variacion.toFixed(1)} %)
            </>
          )}
          {salta && <span className="font-semibold"> · conviene revisar antes de emitir</span>}
        </p>
      )}

      {previa.conIncidencia > 0 && (
        <details className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2">
          <summary className="cursor-pointer text-[11px] font-semibold uppercase text-amber-800">
            {previa.conIncidencia.toLocaleString('es-EC')} predios se quedan fuera
          </summary>

          <ul className="mt-2 space-y-0.5 text-[10px] uppercase text-amber-900">
            {previa.incidencias.map((i) => (
              <li key={i}>{i}</li>
            ))}
          </ul>
        </details>
      )}

      {previa.porDestino.length > 0 && (
        <details className="rounded-lg border border-armazon-200 px-3 py-2">
          <summary className="cursor-pointer text-[11px] font-semibold uppercase text-armazon-600">
            Desglose por destino
          </summary>
          <ul className="mt-2 space-y-0.5">
            {previa.porDestino.map((d) => (
              <li
                key={d.destino}
                className="flex justify-between gap-3 text-[10px] uppercase text-armazon-600"
              >
                <span className="truncate">
                  {nombreLegible(d.destino)}{' '}
                  <span className="text-armazon-400">({d.predios.toLocaleString('es-EC')})</span>
                </span>
                <span className="importe shrink-0 font-semibold text-armazon-800">
                  ${d.total.toFixed(2)}
                </span>
              </li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
}
